import { useState } from 'react';
import { toast } from 'sonner';

import { CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE } from '@/entities/campaign';
import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Textarea } from '@/shared/components/shadcn/textarea';

import type { PostCsvPreviewState } from '../../hooks';
import { CampaignPostImportPreview } from '../ui';

type Props = {
  onApplyBulkPaste: (preview: PostCsvPreviewState) => void;
  onGetBulkPastePreview: (text: string) => PostCsvPreviewState | undefined;
  remainingSlots: number;
};

export function CampaignPostBulkPasteDialog({
  onApplyBulkPaste,
  onGetBulkPastePreview,
  remainingSlots,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [preview, setPreview] = useState<PostCsvPreviewState>();

  const resetDialog = () => {
    setInputValue('');
    setPreview(undefined);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetDialog();
    }
  };

  const handleInputChange = (value: string) => {
    const previousUnsupportedPlatformCount = preview?.unsupportedPlatformCount ?? 0;
    const nextPreview = onGetBulkPastePreview(value);

    setInputValue(value);
    setPreview(nextPreview);

    if ((nextPreview?.unsupportedPlatformCount ?? 0) > previousUnsupportedPlatformCount) {
      toast.warning(CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE);
    }
  };

  const handleConfirm = () => {
    if (!preview || preview.summary.added === 0) return;
    onApplyBulkPaste(preview);
    handleOpenChange(false);
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setIsOpen(true)}
        disabled={remainingSlots === 0}
      >
        Bulk paste
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-170 gap-6">
          <DialogHeader>
            <DialogTitle>Paste post URLs in bulk</DialogTitle>
            <DialogDescription>Paste post URLs, one per line</DialogDescription>
          </DialogHeader>

          <Textarea
            value={inputValue}
            onChange={(event) => handleInputChange(event.target.value)}
            placeholder="https://www.tiktok.com/@creator/video/7123456789012345678&#10;https://www.instagram.com/reel/ABC123DEF45"
            className="min-h-45 resize-none text-sm leading-6"
          />

          <CampaignPostImportPreview
            count={preview?.summary.added ?? 0}
            label="Post URLs to add:"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!preview || preview.summary.added === 0}>
              Add post URLs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
